import {
  HHBox,
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { ArrowBack, CloudOff, Code, Refresh } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { AceEditorComponent } from '../../hinge-connect/rules/components/ace-editor-component';
import { useGetRuleQuery } from '../../hinge-connect/rules/types';
import { omitTypename } from '../../hinge-connect/utils/omit-typename';

type ProviderRuleJsonViewProps = {
  ruleId: string;
  onBack: () => void;
};

export const ProviderRuleJsonView = ({
  ruleId,
  onBack,
}: ProviderRuleJsonViewProps): JSX.Element => {
  const {
    data: ruleData,
    loading,
    error,
  } = useGetRuleQuery({
    variables: { id: ruleId },
  });

  const [jsonValue, setJsonValue] = useState('');

  const rule = ruleData?.getRule;

  // Format the rule data for JSON display
  useEffect(() => {
    if (rule) {
      const cleanRule = omitTypename(rule);
      setJsonValue(JSON.stringify(cleanRule, null, 2));
    }
  }, [rule]);

  if (loading) {
    return (
      <HHBox paddingX="0" bgcolor="#ffffff">
        <HHStack direction="column" spacing={8} height="100%">
          {/* Back Navigation */}
          <HHBox paddingX="2rem" paddingTop="1rem">
            <HHButton
              hhVariant="text"
              startIcon={<ArrowBack />}
              onClick={onBack}
              sx={{
                color: '#1565C0',
                fontWeight: 500,
                textTransform: 'none',
                padding: '6px 12px',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#E3F2FD',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Back to Rule Details
            </HHButton>
          </HHBox>

          {/* Loading Section */}
          <HHBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingY: 8,
              paddingX: '2rem',
              textAlign: 'center',
              minHeight: '400px',
            }}
          >
            <HHTypography
              hhVariant="h5"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                marginBottom: 1,
              }}
            >
              Loading Rule JSON
            </HHTypography>
            <HHTypography
              hhVariant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: '400px',
                lineHeight: 1.6,
              }}
            >
              Fetching rule configuration data...
            </HHTypography>
          </HHBox>
        </HHStack>
      </HHBox>
    );
  }

  if (error || !rule) {
    return (
      <HHBox paddingX="0" bgcolor="#ffffff">
        <HHStack direction="column" spacing={6} height="100%">
          {/* Back Navigation */}
          <HHBox paddingX="2rem" paddingTop="1rem">
            <HHButton
              hhVariant="text"
              startIcon={<ArrowBack />}
              onClick={onBack}
              sx={{
                color: '#1565C0',
                fontWeight: 500,
                textTransform: 'none',
                padding: '6px 12px',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#E3F2FD',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Back to Rule Details
            </HHButton>
          </HHBox>

          {/* Error Content */}
          <HHBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingY: 8,
              paddingX: '2rem',
              textAlign: 'center',
              minHeight: '500px',
            }}
          >
            {/* Error Icon */}
            <HHBox
              sx={{
                backgroundColor: '#FFEBEE',
                background: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
                borderRadius: '50%',
                padding: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 3,
                boxShadow: '0 8px 32px rgba(244, 67, 54, 0.15)',
                border: '2px solid #FFCDD2',
              }}
            >
              <CloudOff
                sx={{
                  fontSize: '64px',
                  color: '#D32F2F',
                }}
              />
            </HHBox>

            {/* Error Title */}
            <HHTypography
              hhVariant="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '28px', sm: '32px', md: '36px' },
                lineHeight: 1.2,
                color: '#D32F2F',
                marginBottom: 2,
                letterSpacing: '-0.02em',
              }}
            >
              JSON Data Unavailable
            </HHTypography>

            {/* Error Description */}
            <HHTypography
              hhVariant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: '18px',
                lineHeight: 1.6,
                maxWidth: '500px',
                marginBottom: 4,
              }}
            >
              We couldn't load the rule's JSON configuration. This might be due
              to a network issue or the rule data may no longer be accessible.
            </HHTypography>

            {/* Error Details */}
            <HHBox
              sx={{
                backgroundColor: '#F5F5F5',
                border: '1px solid #E0E0E0',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: 4,
                maxWidth: '500px',
                width: '100%',
              }}
            >
              <HHStack direction="column" spacing={2}>
                <HHTypography
                  hhVariant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: '16px',
                    color: 'text.primary',
                  }}
                >
                  What you can try:
                </HHTypography>
                <HHStack direction="column" spacing={1} alignItems="flex-start">
                  <HHStack direction="row" alignItems="center" spacing={2}>
                    <HHBox
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#D32F2F',
                      }}
                    />
                    <HHTypography
                      hhVariant="body2"
                      sx={{ color: 'text.secondary', fontSize: '14px' }}
                    >
                      Check your internet connection
                    </HHTypography>
                  </HHStack>
                  <HHStack direction="row" alignItems="center" spacing={2}>
                    <HHBox
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#D32F2F',
                      }}
                    />
                    <HHTypography
                      hhVariant="body2"
                      sx={{ color: 'text.secondary', fontSize: '14px' }}
                    >
                      Refresh the page and try again
                    </HHTypography>
                  </HHStack>
                  <HHStack direction="row" alignItems="center" spacing={2}>
                    <HHBox
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#D32F2F',
                      }}
                    />
                    <HHTypography
                      hhVariant="body2"
                      sx={{ color: 'text.secondary', fontSize: '14px' }}
                    >
                      Return to rule details and try again
                    </HHTypography>
                  </HHStack>
                </HHStack>
              </HHStack>
            </HHBox>

            {/* Action Buttons */}
            <HHStack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              justifyContent="center"
            >
              <HHButton
                hhVariant="contained"
                startIcon={<Refresh />}
                onClick={(): void => window.location.reload()}
                sx={{
                  backgroundColor: '#D32F2F',
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#B71C1C',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Try Again
              </HHButton>
              <HHButton
                hhVariant="outlined"
                onClick={onBack}
                sx={{
                  borderColor: '#D32F2F',
                  color: '#D32F2F',
                  fontWeight: 600,
                  textTransform: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#FFEBEE',
                    borderColor: '#B71C1C',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                Go Back
              </HHButton>
            </HHStack>

            {/* Technical Details (if available) */}
            {error && (
              <HHBox
                sx={{
                  marginTop: 4,
                  padding: '16px',
                  backgroundColor: '#FFF3E0',
                  border: '1px solid #FFB74D',
                  borderRadius: '8px',
                  maxWidth: '500px',
                  width: '100%',
                }}
              >
                <HHTypography
                  hhVariant="caption"
                  sx={{
                    color: '#E65100',
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    wordBreak: 'break-word',
                  }}
                >
                  Error: {error.message || 'Unknown error occurred'}
                </HHTypography>
              </HHBox>
            )}
          </HHBox>
        </HHStack>
      </HHBox>
    );
  }

  return (
    <HHBox paddingX="0" bgcolor="#ffffff">
      <HHStack direction="column" spacing={4} height="100%">
        {/* Back Navigation */}
        <HHBox paddingX="2rem" paddingTop="1rem">
          <HHButton
            hhVariant="text"
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{
              color: '#1565C0',
              fontWeight: 500,
              textTransform: 'none',
              padding: '6px 12px',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: '#E3F2FD',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Back to Rule Details
          </HHButton>
        </HHBox>

        {/* Header */}
        <HHBox
          sx={{
            position: 'relative',
            marginBottom: '16px',
            paddingX: '2rem',
          }}
        >
          <HHStack direction="row" alignItems="center" spacing={3}>
            <HHBox
              sx={{
                backgroundColor: '#2E3440',
                background: 'linear-gradient(135deg, #2E3440 0%, #3B4252 100%)',
                borderRadius: '16px',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(46, 52, 64, 0.25)',
              }}
            >
              <Code
                sx={{
                  fontSize: '32px',
                  color: 'white',
                }}
              />
            </HHBox>
            <HHStack direction="column" spacing={0.5} sx={{ flex: 1 }}>
              <HHTypography
                hhVariant="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '24px', sm: '28px', md: '32px' },
                  lineHeight: 1.2,
                  background:
                    'linear-gradient(135deg, #2E3440 0%, #3B4252 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent',
                  letterSpacing: '-0.02em',
                }}
              >
                {rule.definition.name} - JSON
              </HHTypography>
              <HHTypography
                hhVariant="body1"
                sx={{
                  color: 'text.secondary',
                  fontSize: '16px',
                  fontWeight: 400,
                  marginTop: '4px',
                }}
              >
                Raw rule configuration data
              </HHTypography>
            </HHStack>
          </HHStack>

          {/* Decorative accent line */}
          <HHBox
            sx={{
              position: 'absolute',
              bottom: '-12px',
              left: '34px',
              width: '120px',
              height: '4px',
              background: 'linear-gradient(135deg, #2E3440 0%, #3B4252 100%)',
              borderRadius: '2px',
              opacity: 0.6,
            }}
          />
        </HHBox>

        {/* JSON Editor */}
        <HHBox paddingX="2rem" paddingBottom="2rem">
          <HHBox
            sx={{
              border: '1px solid #E0E0E0',
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: '#f8f9fa',
              margin: 2,
              padding: 2,
            }}
          >
            <AceEditorComponent
              label=""
              value={jsonValue}
              helperText="Complete rule configuration in JSON format"
              onChange={(): void => {
                // This is readonly, so no-op
              }}
              dataTestId="rule-json-editor"
              readonly={true}
            />
          </HHBox>
        </HHBox>
      </HHStack>
    </HHBox>
  );
};
